import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { STORAGE_KEYS } from '../core/constants';

export type SupportedLanguage = 'en' | 'nl' | 'fr';

export interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  readonly supportedLanguages: LanguageOption[] = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
  ];

  private currentLanguageSignal = signal<SupportedLanguage>('nl');
  readonly currentLanguage = this.currentLanguageSignal.asReadonly();

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    this.translate.addLangs(['en', 'nl', 'fr']);
    this.translate.setDefaultLang('nl');

    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) as SupportedLanguage | null;
    
    let language: SupportedLanguage = 'nl';

    if (savedLanguage && this.isSupported(savedLanguage)) {
      language = savedLanguage;
    } else {
      // Auto-detect from system/browser when no user preference is saved.
      const browserLang = this.translate.getBrowserLang()?.toLowerCase();
      const browserLangCode = browserLang?.split('-')[0];
      if (browserLangCode && this.isSupported(browserLangCode)) {
        language = browserLangCode;
      }
    }

    this.setLanguage(language);
  }

  setLanguage(lang: SupportedLanguage): void {
    if (!this.isSupported(lang)) {
      return;
    }

    this.translate.use(lang);
    this.currentLanguageSignal.set(lang);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
    document.documentElement.lang = lang;
  }

  private isSupported(lang: string): lang is SupportedLanguage {
    return this.supportedLanguages.some((l) => l.code === lang);
  }

  getCurrentLanguageOption(): LanguageOption {
    return (
      this.supportedLanguages.find((l) => l.code === this.currentLanguageSignal()) ??
      this.supportedLanguages[1]
    );
  }

  /** Display name for Cleanno language codes (`en` | `nl` | `fr`) on profiles and lists. */
  nativeNameForLanguageCode(code: string): string {
    const opt = this.supportedLanguages.find((l) => l.code === code);
    return opt?.nativeName ?? code.toUpperCase();
  }
}

